/**
 * Flora destruction tracking: counts destroyed flora per team/player/planet.
 * Pure functions — no side effects, no rendering.
 */

import type { FloraState, FloraDestructionState, PlanetState, Vec3 } from '../types';
import { vecSub, vecLength } from '../math';

/**
 * Initialize flora destruction tracking from initial flora state.
 */
export function initFloraDestructionState(flora: FloraState[]): FloraDestructionState {
  const perPlanetInitial: Record<number, number> = {};
  for (const f of flora) {
    perPlanetInitial[f.planetIndex] = (perPlanetInitial[f.planetIndex] ?? 0) + 1;
  }

  return {
    totalDestroyed: 0,
    perTeam: {},
    perPlayer: {},
    perPlanet: {},
    initialCount: flora.length,
    perPlanetInitial,
  };
}

/**
 * Check which flora items are destroyed by a blast.
 * Returns IDs of destroyed flora and updated flora array.
 */
export function checkFloraDestruction(
  flora: FloraState[],
  impactPos: Vec3,
  blastRadius: number,
  planets: PlanetState[]
): { destroyedIds: number[]; updatedFlora: FloraState[] } {
  const destroyedIds: number[] = [];
  const updatedFlora = flora.map(f => {
    if (!f.alive) return f;

    const planet = planets[f.planetIndex];
    if (!planet) return f;

    // Get world position of flora
    const floraWorldPos: Vec3 = {
      x: planet.position.x + f.surfaceDir.x * planet.radius,
      y: planet.position.y + f.surfaceDir.y * planet.radius,
      z: planet.position.z + f.surfaceDir.z * planet.radius,
    };

    const dist = vecLength(vecSub(floraWorldPos, impactPos));
    if (dist <= blastRadius) {
      destroyedIds.push(f.id);
      return { ...f, alive: false };
    }
    return f;
  });

  return { destroyedIds, updatedFlora };
}

/**
 * Record flora destruction in the tracking state.
 */
export function recordFloraDestruction(
  state: FloraDestructionState,
  planetIndex: number,
  playerId: number,
  teamIndex: number
): FloraDestructionState {
  return {
    ...state,
    totalDestroyed: state.totalDestroyed + 1,
    perTeam: {
      ...state.perTeam,
      [teamIndex]: (state.perTeam[teamIndex] ?? 0) + 1,
    },
    perPlayer: {
      ...state.perPlayer,
      [playerId]: (state.perPlayer[playerId] ?? 0) + 1,
    },
    perPlanet: {
      ...state.perPlanet,
      [planetIndex]: (state.perPlanet[planetIndex] ?? 0) + 1,
    },
  };
}

/**
 * Get overall flora health fraction (1.0 = pristine, 0.0 = all destroyed).
 */
export function getFloraHealthFraction(state: FloraDestructionState): number {
  if (state.initialCount === 0) return 1.0;
  return 1.0 - (state.totalDestroyed / state.initialCount);
}

/**
 * Get flora health fraction for a specific planet.
 */
export function getPlanetFloraHealthFraction(
  state: FloraDestructionState,
  planetIndex: number
): number {
  const initial = state.perPlanetInitial[planetIndex] ?? 0;
  if (initial === 0) return 1.0;
  const destroyed = state.perPlanet[planetIndex] ?? 0;
  return 1.0 - (destroyed / initial);
}

/**
 * Get a team's destruction score (higher = more destructive).
 */
export function getTeamDestructionScore(
  state: FloraDestructionState,
  teamIndex: number
): number {
  return state.perTeam[teamIndex] ?? 0;
}
